import { memo, useMemo } from 'react'
import { Layer, Source, type CircleLayer } from 'react-map-gl'
import { PLOT_COLORS } from '#app/consts'
import { buildClusterData } from './cluster-data'

export type ClusterProps = { ownedPlotIds: string[]; selectedPlotIds: string[] }
const scales = {
	small: [9.5, 15, 12.5, 40],
	medium: [6, 1 / 6, 9.5, 1 / 1.5],
	large: [1, 1 / 750, 6, 1 / 100],
	largest: [1, 1 / 30000, 3, 1 / 9000],
} as const
export const ClusterLayer = memo(function ClusterLayer({
	ownedPlotIds,
	selectedPlotIds,
	size,
}: ClusterProps & { size: keyof typeof scales }) {
	const data = useMemo(
		() => buildClusterData(ownedPlotIds, selectedPlotIds),
		[ownedPlotIds, selectedPlotIds],
	)
	const [minZoom, minScale, maxZoom, maxScale] = scales[size]
	const radius: NonNullable<CircleLayer['paint']>['circle-radius'] = [
		'interpolate',
		['linear'],
		['zoom'],
		minZoom,
		['*', ['get', 'area'], minScale],
		maxZoom,
		['*', ['get', 'area'], maxScale],
	]
	if (!data.features.length) return null
	return (
		<Source id={`clusters-${size}`} type="geojson" data={data}>
			<Layer
				id={`clusters-${size}-circle-layer`}
				type="circle"
				filter={['==', ['get', 'selected'], false]}
				paint={{
					'circle-color': PLOT_COLORS.ownedFill,
					'circle-opacity': 0.3,
					'circle-radius': radius,
				}}
			/>
			<Layer
				id={`clusters-${size}-selected-circle-layer`}
				type="circle"
				filter={['==', ['get', 'selected'], true]}
				paint={{
					'circle-color': PLOT_COLORS.selectedFill,
					'circle-opacity': 0.2,
					'circle-stroke-color': PLOT_COLORS.selectedLine,
					'circle-stroke-width': 1,
					'circle-stroke-opacity': 1,
					'circle-radius': radius,
				}}
			/>
		</Source>
	)
})
